import React, { useEffect, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';

const formatDate = (dateStr) => {
  if (!dateStr) return 'Date TBD';
  return new Date(dateStr).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const ticketCodeOf = (t) =>
  t?.ticket_code || t?.id?.slice(0, 8).toUpperCase() || '——';

const qrValueOf = (t) =>
  JSON.stringify({
    ticketCode: ticketCodeOf(t),
    registrationId: t?.id,
    eventId: t?.events?.id,
    attendeeName: t?.attendee_name,
    attendeeEmail: t?.attendee_email,
  });

// Full-screen, scan-optimized, swipeable ticket viewer for the native app.
const MobileTicketView = ({ tickets = [], initialIndex = 0, onClose }) => {
  const scrollRef = useRef(null);
  const [index, setIndex] = useState(initialIndex);

  // Jump to the initially selected ticket once mounted.
  useEffect(() => {
    const el = scrollRef.current;
    if (el && initialIndex > 0) {
      el.scrollLeft = initialIndex * el.clientWidth;
    }
  }, [initialIndex]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    if (i !== index) setIndex(i);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-gray-100 dark:bg-gray-950 flex flex-col">
      {/* Top bar */}
      <div
        className="flex items-center justify-between px-4 pb-3 bg-white dark:bg-gray-900 shadow-sm"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)' }}
      >
        <button
          onClick={onClose}
          className="w-10 h-10 -ml-2 flex items-center justify-center rounded-full text-gray-600 dark:text-gray-300 active:bg-gray-100 dark:active:bg-gray-800"
          aria-label="Close"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <span className="text-sm font-semibold text-gray-900 dark:text-white">
          {tickets.length > 1 ? `Ticket ${index + 1} of ${tickets.length}` : 'Your Ticket'}
        </span>
        <span className="w-10" />
      </div>

      {/* Swipeable tickets */}
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="flex-1 flex overflow-x-auto snap-x snap-mandatory"
        style={{ scrollbarWidth: 'none' }}
      >
        {tickets.map((t) => {
          const ev = t?.events || {};
          const paid = t?.payment_status === 'paid';
          const checkedIn = t?.status === 'attended';
          return (
            <div key={t.id} className="snap-center shrink-0 w-full overflow-y-auto px-5 py-6">
              <div className="max-w-sm mx-auto bg-white dark:bg-gray-900 rounded-3xl shadow-xl overflow-hidden">
                {/* Header band */}
                <div className="bg-gradient-to-br from-primary-dark to-primary px-5 py-5 text-white">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold tracking-wider text-white/70">
                      {ev.org || 'EVENT TICKET'}
                    </span>
                    {paid && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/20">
                        PAID
                      </span>
                    )}
                  </div>
                  <h2 className="mt-1 font-display text-xl font-bold leading-snug">
                    {ev.event || ev.title || 'Event'}
                  </h2>
                </div>

                {/* QR */}
                <div className="px-5 pt-6 pb-4 flex flex-col items-center">
                  {checkedIn && (
                    <span className="mb-3 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 011.4-1.4l3.1 3.1 6.8-6.8a1 1 0 011.4 0z" clipRule="evenodd" />
                      </svg>
                      Checked in
                    </span>
                  )}
                  <div className="bg-white p-4 rounded-2xl ring-1 ring-gray-100">
                    <QRCodeCanvas
                      id={`qr-canvas-${t.id}`}
                      value={qrValueOf(t)}
                      size={232}
                      level="H"
                      includeMargin={false}
                    />
                  </div>
                  <p className="mt-3 text-xs text-gray-400">Show this code at check-in</p>
                  <p className="font-mono text-base font-bold tracking-widest text-gray-900 dark:text-white">
                    {ticketCodeOf(t)}
                  </p>
                </div>

                {/* Perforation */}
                <div className="relative">
                  <div className="border-t-2 border-dashed border-gray-200 dark:border-gray-700 mx-5" />
                  <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-950" />
                  <div className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-950" />
                </div>

                {/* Details */}
                <div className="px-5 py-5 space-y-3 text-sm">
                  <Detail
                    icon="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    label="Date"
                    value={`${formatDate(ev.start)}${formatTime(ev.start) ? ` · ${formatTime(ev.start)}` : ''}`}
                  />
                  {(ev.venue || ev.city) && (
                    <Detail
                      icon="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z|M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      label="Location"
                      value={[ev.venue, ev.city].filter(Boolean).join(', ')}
                    />
                  )}
                  {t?.attendee_name && (
                    <Detail
                      icon="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      label="Attendee"
                      value={t.attendee_name}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Dots */}
      {tickets.length > 1 && (
        <div className="flex justify-center gap-1.5 py-3">
          {tickets.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? 'w-5 bg-primary' : 'w-1.5 bg-gray-300 dark:bg-gray-700'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Detail = ({ icon, label, value }) => {
  const paths = icon.split('|');
  return (
    <div className="flex items-start gap-3">
      <svg className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        {paths.map((d, i) => (
          <path key={i} strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
        ))}
      </svg>
      <div className="min-w-0">
        <p className="text-xs text-gray-400">{label}</p>
        <p className="font-medium text-gray-900 dark:text-white">{value}</p>
      </div>
    </div>
  );
};

export default MobileTicketView;
